import { Button, Col, Input, message, Popconfirm, Row, Select, Space, Table } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '@ant-design/pro-components';
import {
  getSmeStaffProductsThunk,
  deleteSmeStaffProductByIdThunk,
} from '../../services/staffProductService/staffProductThunk';
import { AppDispatch, RootState } from '../../store/store';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import SmeStaffProductActionModal from './SmeStaffProductFormModal';

const { Option } = Select;
const { Search } = Input;

const SmeStaffProductMaintenancePage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { smeStaffProducts } = useSelector((state: RootState) => state.staffProduct);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('code');

  const tablePagination = {
    total: smeStaffProducts.data.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Referral Code',
      dataIndex: 'referralCode',
      key: 'referralCode',
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          {!smeStaffProducts.isLoading && (
            <SmeStaffProductActionModal
              key={record?.id}
              product={record}
              callback={() => fetchSmeStaffProducts(initialParams)}
            />
          )}
          <Popconfirm
            title="Do you want to delete this product?"
            onConfirm={() => onDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" loading={deleteLoading} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchSmeStaffProducts = async (params?: any) => {
    await dispatch(getSmeStaffProductsThunk({ params }));
  };

  const onSearch = (value: string) => {
    value = value.trim();
    fetchSmeStaffProducts({
      ...initialParams,
      // filters: { ...filterState, [searchFiled]: value },
      search: { [searchFiled]: value },
    });
    // setFilterState({ ...initialParams.filters, [searchFiled]: value });
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    fetchSmeStaffProducts({ ...initialParams, ...filterState, pagination });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  const onDelete = async (id: string) => {
    setDeleteLoading(true);
    try {
      await dispatch(deleteSmeStaffProductByIdThunk({ id })).unwrap();
      message.success('Product deleted successfully');
      fetchSmeStaffProducts(initialParams);
    } catch (error: any) {
      console.error(
        `Failed to delete staff product: ${error?.response?.data?.message || error?.message}`
      );
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to delete staff product'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchSmeStaffProducts(initialParams);
  }, [initialParams]);

  return (
    <>
      <Helmet>
        <title>Staff Product Config - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Staff Product Config" />
      <Row justify="space-between">
        <Col>
          <Input.Group compact>
            <Select
              defaultValue="code"
              style={{ width: 200 }}
              onChange={(value) => setSearchFiled(value)}
            >
              {Array.isArray(columns) &&
                columns
                  .filter((col: any) => col.key !== 'action')
                  .map((col: any) => (
                    <Option key={col.key} value={col.dataIndex}>
                      {col.title?.toString()}
                    </Option>
                  ))}
            </Select>
            <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
          </Input.Group>
        </Col>
        <Col>
          <SmeStaffProductActionModal callback={() => fetchSmeStaffProducts(initialParams)} />
        </Col>
      </Row>

      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={smeStaffProducts.data.data}
        loading={smeStaffProducts.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default SmeStaffProductMaintenancePage;
